import { useMemo } from 'react';
import type { FlowId, Step, StepId } from '../../types';

interface CompletionDetectionOptions {
  flowId: FlowId;
  sessionId: string | null;
  steps: Partial<Record<StepId, Step>>;
  currentStepId: StepId;
  selfServiceData: { isCompleted?: boolean };
  storeHasCompletedFlow: boolean;
  completedFlowCache: Set<FlowId>;
}

interface CompletionDetectionResult {
  isCompletionStep: boolean;
  hasCompletedFlow: boolean;
  completionStepIds: Set<StepId>;
  completionFlagKey: string;
}

const getCompletionStepIds = (steps: Partial<Record<StepId, Step>>): Set<StepId> => {
  const ids = new Set<StepId>();

  Object.values(steps).forEach((step) => {
    if (!step) {
      return;
    }

    const shouldFlag =
      step.phase === 'completion' ||
      step.id === 'confirmation' ||
      step.id.endsWith('_confirmation');

    if (shouldFlag) {
      ids.add(step.id as StepId);
    }
  });

  return ids;
};

const getCompletionFlagKey = (sessionId: string | null, flowId: FlowId): string => {
  if (sessionId) {
    return `wizardCompleted:${sessionId}`;
  }
  return `wizardCompleted:flow:${flowId}`;
};

const hasStoredCompletionFlag = (
  completionFlagKey: string,
  flowCompletionKey: string,
): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    if (window.sessionStorage.getItem(completionFlagKey) === 'true') {
      return true;
    }
    return window.sessionStorage.getItem(flowCompletionKey) === 'true';
  } catch {
    return false;
  }
};

const hasCompletionInStoredState = (
  sessionId: string | null,
  completionStepIds: Set<StepId>,
): boolean => {
  if (!sessionId || typeof window === 'undefined') {
    return false;
  }

  try {
    const stored = window.sessionStorage.getItem(`wizardState:${sessionId}`);
    if (!stored) {
      return false;
    }

    const state = JSON.parse(stored) as {
      currentStepId?: StepId;
      history?: StepId[];
    };

    if (state.currentStepId && completionStepIds.has(state.currentStepId)) {
      return true;
    }

    if (Array.isArray(state.history)) {
      return state.history.some((step) => completionStepIds.has(step));
    }
  } catch {
    return false;
  }

  return false;
};

export const useCompletionDetection = ({
  flowId,
  sessionId,
  steps,
  currentStepId,
  selfServiceData,
  storeHasCompletedFlow,
  completedFlowCache,
}: CompletionDetectionOptions): CompletionDetectionResult => {
  const completionStepIds = useMemo(() => getCompletionStepIds(steps), [steps]);

  const isCompletionStep = completionStepIds.has(currentStepId);

  const completionFlagKey = useMemo(
    () => getCompletionFlagKey(sessionId, flowId),
    [flowId, sessionId],
  );

  const sessionCompletionKey = sessionId ? `wizardCompleted:${sessionId}` : null;
  const flowCompletionKey = `wizardCompleted:flow:${flowId}`;

  const storedFlag = useMemo(
    () => hasStoredCompletionFlag(completionFlagKey, flowCompletionKey),
    [completionFlagKey, flowCompletionKey],
  );

  const completionInState = useMemo(
    () => hasCompletionInStoredState(sessionId, completionStepIds),
    [completionStepIds, sessionId],
  );

  const hasCompletedFlow = useMemo(() => {
    if (completedFlowCache.has(flowId)) {
      return true;
    }

    if (storeHasCompletedFlow) {
      return true;
    }

    if (selfServiceData.isCompleted === true) {
      return true;
    }

    if (storedFlag) {
      return true;
    }

    return completionInState;
  }, [
    flowId,
    completionInState,
    storedFlag,
    selfServiceData.isCompleted,
    storeHasCompletedFlow,
    completedFlowCache,
  ]);

  return {
    isCompletionStep,
    hasCompletedFlow,
    completionStepIds,
    completionFlagKey,
  };
};
